import { CollectionScope, RealTimeResource } from '@nemuru-eng/js-api-client';
import { useEffect, useRef } from 'react';

import { useOperationsContext } from '@/contexts/OperationsContext/OperationsContext';
import { useRealTimeResource } from '@/hooks/useRealTimeResource';

/** Quiet stream: fires this long after the last event. */
const REFRESH_DEBOUNCE_MS = 2000;
/** Continuous stream: fires at most this often, even if events keep arriving. */
const REFRESH_MAX_WAIT_MS = 5000;

/** Bumps `metricsRefreshToken` (debounced, with a max wait) on every metrics event of the given scope; widgets refetch on it. */
export const useLiveMetrics = (scope?: CollectionScope) => {
  const {
    state: { metricsRefreshToken },
    actions: { updateOperationsContext },
  } = useOperationsContext();
  const tokenRef = useRef(metricsRefreshToken);
  tokenRef.current = metricsRefreshToken;
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const firstEventAt = useRef<number>();

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const { state } = useRealTimeResource({
    resource: RealTimeResource.METRICS,
    id: scope,
    enabled: Boolean(scope),
    onEvent: () => {
      const now = Date.now();
      firstEventAt.current ??= now;
      if (timer.current) clearTimeout(timer.current);
      const waited = now - firstEventAt.current;
      const delay = Math.max(0, Math.min(REFRESH_DEBOUNCE_MS, REFRESH_MAX_WAIT_MS - waited));
      timer.current = setTimeout(() => {
        timer.current = undefined;
        firstEventAt.current = undefined;
        updateOperationsContext({ metricsRefreshToken: tokenRef.current + 1 });
      }, delay);
    },
  });

  return { state };
};
